/**
 * BlogPost - Individual blog post page
 */

import { useEffect, useMemo, Fragment } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, User } from 'lucide-react'
import { FadeIn, ReadingProgress } from '@/components/motion'
import { getBlogPostBySlug, BlogVideo } from '@/pages/blog/blogData'
import BlogVideoComponent from '@/pages/blog/BlogVideo'
import ReactMarkdown from 'react-markdown'
import { BlogShareButtons } from '@/components/BlogShareButtons'
import { NewsletterSignup } from '@/components/NewsletterSignup'
import { DonationWidget } from '@/components/DonationWidget'
import { setPageMeta } from '@/utils/seo'

// Helper to extract text content from React children
function getTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (Array.isArray(children)) return children.map(getTextContent).join('')
  if (children && typeof children === 'object' && 'props' in children) {
    return getTextContent((children as any).props.children)
  }
  return ''
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getBlogPostBySlug(slug) : undefined

  // Build a map of heading text to videos that should appear after them
  const videosByHeading = useMemo(() => {
    const map = new Map<string, BlogVideo[]>()
    if (post?.videos) {
      for (const video of post.videos) {
        if (video.placement === 'after-heading' && video.afterHeading) {
          const existing = map.get(video.afterHeading) || []
          existing.push(video)
          map.set(video.afterHeading, existing)
        }
      }
    }
    return map
  }, [post?.videos])

  useEffect(() => {
    if (post) {
      const baseUrl = 'https://returned.com'
      const postUrl = `${baseUrl}/blog/${post.slug}`
      const imageUrl = post.heroImage || `${baseUrl}/og-default.jpg`

      setPageMeta({
        title: `${post.title} | Returned.com Blog`,
        description: post.excerpt,
        canonical: postUrl,
        robots: 'index, follow, max-image-preview:large, max-snippet:-1',
      })

      // Helper to set or create meta tag
      const setMetaTag = (selector: string, attribute: string, content: string) => {
        let tag = document.querySelector(selector)
        if (!tag) {
          tag = document.createElement('meta')
          const [attr, value] = selector.match(/\[([^=]+)="([^"]+)"\]/)?.slice(1) || []
          if (attr && value) tag.setAttribute(attr, value)
          document.head.appendChild(tag)
        }
        tag.setAttribute(attribute, content)
      }

      // Open Graph
      setMetaTag('meta[property="og:type"]', 'content', 'article')
      setMetaTag('meta[property="og:url"]', 'content', postUrl)
      setMetaTag('meta[property="og:title"]', 'content', post.title)
      setMetaTag('meta[property="og:description"]', 'content', post.excerpt)
      setMetaTag('meta[property="og:image"]', 'content', imageUrl)
      setMetaTag('meta[property="og:site_name"]', 'content', 'Returned.com')
      setMetaTag('meta[property="article:published_time"]', 'content', new Date(post.date).toISOString())
      setMetaTag('meta[property="article:author"]', 'content', post.author)

      // Twitter Card
      setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image')
      setMetaTag('meta[name="twitter:url"]', 'content', postUrl)
      setMetaTag('meta[name="twitter:title"]', 'content', post.title)
      setMetaTag('meta[name="twitter:description"]', 'content', post.excerpt)
      setMetaTag('meta[name="twitter:image"]', 'content', imageUrl)

      // JSON-LD Structured Data (BlogPosting schema)
      let scriptTag = document.getElementById('blogpost-jsonld')
      if (!scriptTag) {
        scriptTag = document.createElement('script')
        scriptTag.setAttribute('type', 'application/ld+json')
        scriptTag.id = 'blogpost-jsonld'
        document.head.appendChild(scriptTag)
      }
      scriptTag.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: imageUrl,
        datePublished: new Date(post.date).toISOString(),
        author: {
          '@type': 'Person',
          name: post.author,
          jobTitle: post.authorRole,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Returned.com',
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/favicon.svg`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': postUrl,
        },
        keywords: post.tags.join(', '),
      })
    }
    // Scroll to top on load
    window.scrollTo(0, 0)

    return () => document.getElementById('blogpost-jsonld')?.remove()
  }, [post])

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  // Component to render a heading with optional video(s) after it
  const HeadingWithVideo = ({
    level,
    children,
    style,
  }: {
    level: 2 | 3
    children: React.ReactNode
    style: React.CSSProperties
  }) => {
    const text = getTextContent(children)
    const videos = videosByHeading.get(text) || []
    const Tag = level === 2 ? 'h2' : 'h3'

    return (
      <Fragment>
        <Tag style={style}>{children}</Tag>
        {videos.map((video) => (
          <BlogVideoComponent
            key={video.id}
            postSlug={post.slug}
            videoId={video.id}
            caption={video.caption}
          />
        ))}
      </Fragment>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
      <ReadingProgress />
      {/* Article Header */}
      <section className="landing-section" style={{ paddingBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <FadeIn>
            {/* Back Link */}
            <Link
              to="/blog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6366f1',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                marginBottom: '2rem',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#818cf8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6366f1'}
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>

            {/* Category Badge */}
            <span style={{
              background: 'rgba(99, 102, 241, 0.10)',
              color: '#4f46e5',
              fontSize: '0.75rem',
              fontWeight: 600,
              padding: '0.25rem 0.75rem',
              borderRadius: '9999px',
              display: 'inline-block',
              marginBottom: '1rem',
            }}>
              {post.category}
            </span>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 700,
              color: '#0f0f0f',
              marginBottom: '1.5rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}>
              {post.title}
            </h1>

            {/* Meta Info */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.5rem',
              color: '#5f5f5f',
              fontSize: '0.9rem',
              marginBottom: '2rem',
              paddingBottom: '2rem',
              borderBottom: '1px solid #eaeaea',
            }}>
              <Link
                to="/paul-lin"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <User size={16} />
                <span style={{ color: '#5f5f5f', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#5f5f5f'}
                >{post.author}</span>
                <span style={{ color: '#6b6b6b' }}>·</span>
                <span style={{ color: '#6b6b6b' }}>{post.authorRole}</span>
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span>{post.date}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span>{post.readTime}</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Article Content */}
      <section style={{ padding: '0 1rem clamp(2rem, 5vw, 4rem)' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <FadeIn delay={0.1}>
            <article className="blog-content" style={{
              color: '#3f3f3f',
              fontSize: '1.1rem',
              lineHeight: 1.8,
            }}>
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <HeadingWithVideo
                      level={2}
                      style={{
                        fontSize: 'clamp(1.5rem, 3vw, 1.75rem)',
                        fontWeight: 700,
                        color: '#0f0f0f',
                        marginTop: '2.5rem',
                        marginBottom: '1rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {children}
                    </HeadingWithVideo>
                  ),
                  h3: ({ children }) => (
                    <HeadingWithVideo
                      level={3}
                      style={{
                        fontSize: 'clamp(1.25rem, 2.5vw, 1.4rem)',
                        fontWeight: 600,
                        color: '#0f0f0f',
                        marginTop: '2rem',
                        marginBottom: '0.75rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {children}
                    </HeadingWithVideo>
                  ),
                  p: ({ children }) => (
                    <p style={{
                      marginBottom: '1.5rem',
                    }}>
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul style={{
                      marginBottom: '1.5rem',
                      paddingLeft: '1.5rem',
                    }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol style={{
                      marginBottom: '1.5rem',
                      paddingLeft: '1.5rem',
                    }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{
                      marginBottom: '0.5rem',
                    }}>
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{
                      color: '#0f0f0f',
                      fontWeight: 600,
                    }}>
                      {children}
                    </strong>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      borderLeft: '3px solid #6366f1',
                      paddingLeft: '1.5rem',
                      marginLeft: 0,
                      marginBottom: '1.5rem',
                      fontStyle: 'italic',
                      color: '#4f46e5',
                    }}>
                      {children}
                    </blockquote>
                  ),
                  hr: () => (
                    <hr style={{
                      border: 'none',
                      borderTop: '1px solid #eaeaea',
                      margin: '2.5rem 0',
                    }} />
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      style={{
                        color: '#4f46e5',
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      }}
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {children}
                    </a>
                  ),
                  code: ({ children }) => (
                    <code style={{
                      background: 'rgba(99, 102, 241, 0.15)',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      color: '#4f46e5',
                    }}>
                      {children}
                    </code>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </article>

            {/* Author Card */}
            <Link
              to="/paul-lin"
              style={{
                textDecoration: 'none',
                display: 'block',
              }}
            >
              <div style={{
                background: '#fafafa',
                border: '1px solid #eaeaea',
                borderRadius: '16px',
                padding: '1.5rem',
                marginTop: '2.5rem',
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#eaeaea'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#111111',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  flexShrink: 0,
                }}>
                  {post.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f0f0f', fontWeight: 600 }}>{post.author}</div>
                  <div style={{ color: '#5f5f5f', fontSize: '0.9rem' }}>{post.authorRole}</div>
                </div>
                <ArrowRight size={18} style={{ color: '#6366f1', flexShrink: 0 }} />
              </div>
            </Link>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginTop: '3rem',
                paddingTop: '2rem',
                borderTop: '1px solid #eaeaea',
              }}>
                <Tag size={16} style={{ color: '#6b6b6b', marginRight: '0.5rem' }} />
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      background: '#fafafa',
                      color: '#5f5f5f',
                      fontSize: '0.8rem',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Social Share Buttons */}
            <BlogShareButtons
              url={`https://returned.com/blog/${post.slug}`}
              title={post.title}
              description={post.excerpt}
            />

            {/* Newsletter Signup */}
            <div style={{ marginTop: '2rem' }}>
              <NewsletterSignup variant="inline" source="blog-post" />
            </div>

            {/* Donation Widget */}
            <DonationWidget />

            {/* CTA */}
            <div style={{
              background: 'rgba(99, 102, 241, 0.10)',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              borderRadius: '16px',
              padding: '2rem',
              marginTop: '2rem',
              textAlign: 'center',
            }}>
              <h3 style={{
                color: '#0f0f0f',
                fontSize: '1.25rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
              }}>
                Meet Reeva
              </h3>
              <p style={{
                color: '#5f5f5f',
                marginBottom: '1.5rem',
              }}>
                The AI receptionist that answers every call. Now in closed beta.
              </p>
              <a
                href="https://helloreeva.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: '#111111',
                  color: '#ffffff',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 500,
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Join the Reeva beta
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

    </div>
  )
}
